import { MediumFormRow } from 'framework/ui/layout';
import { useEffect } from 'react';
import { isEqual, pick } from 'lodash';
import {
  DefaultInputPropTypes,
  InputListTemplateProps,
  useFormContext,
  useInputHelper,
  EnumDropdown,
  NumberInput,
} from 'framework/form';
import { Box } from '@mui/material';
import { getLastSource } from 'framework/utils';
import { RowGroupInputsWrapper } from 'framework/ui/ui-builder';
import {
  COMPENSATION_CURRENCY_TYPES,
  EMPLOYEE_CONTRACT_TYPES,
  LOCATION_FOR_HIRING_OPTIONS,
  RELOCATION_OPTION,
  VACANCY_POSITION_SLOT_WORKER_TYPE,
} from 'domain/shared/newVacancy';
import { EMPLOYEE_CONTRACT_ASSIGMENT_TYPES } from 'domain/shared/employee';
import { CURRENCY_TYPES } from 'domain/shared/core/constants';
import {
  VACANCY_POSITION_SLOT_STAFF_CONTRACT_TYPES,
  VACANCY_POSITION_SLOT_CONTRACTOR_CONTRACT_TYPES,
} from './positionSlots/constants';

const VACANCY_POSITION_SLOT_CONTRACTOR_CONTRACT_TYPE_OPTIONS = pick(
  EMPLOYEE_CONTRACT_TYPES,
  VACANCY_POSITION_SLOT_CONTRACTOR_CONTRACT_TYPES,
);

const WorkingConditionsInputs = ({
  isUpdateForm = false,
  props,
  isPositionSlotFlow = false,
}: {
  isUpdateForm: boolean;
  props: DefaultInputPropTypes<any>;
  isPositionSlotFlow?: boolean;
}) => {
  const { getSource, getRawOnChangeCallback, getValue } = useInputHelper(props);
  const { data = {} } = useFormContext();
  const expectedCompensation = getValue() || {};
  const rawOnChangeCallback = getRawOnChangeCallback() || (() => {});
  const selectedPositionSlotScope = isPositionSlotFlow ? data.positionSlotScope : null;
  const selectedPositionSlotWorkerType = selectedPositionSlotScope?.workerType;
  const isHiringLocationVisible = !isPositionSlotFlow;
  const isStaffPositionSlot = selectedPositionSlotWorkerType === VACANCY_POSITION_SLOT_WORKER_TYPE.STAFF;
  const isContractorPositionSlot = selectedPositionSlotWorkerType === VACANCY_POSITION_SLOT_WORKER_TYPE.CONTRACTOR;
  const contractTypeOptions = isContractorPositionSlot
    ? VACANCY_POSITION_SLOT_CONTRACTOR_CONTRACT_TYPE_OPTIONS
    : EMPLOYEE_CONTRACT_TYPES;

  const source = getSource();

  const onChangeCallback = (value: { [x: string]: any }) => {
    rawOnChangeCallback({
      [source]: {
        ...expectedCompensation,
        ...getLastSource(value),
      },
    });
  };

  const onCompensationChangeCallback = (value: { [x: string]: any }) => {
    rawOnChangeCallback({
      [source]: {
        ...expectedCompensation,
        compensation: expectedCompensation.compensation
          ? {
              ...expectedCompensation.compensation,
              ...getLastSource(value),
            }
          : {},
      },
    });
  };

  useEffect(() => {
    if (!getValue()) {
      rawOnChangeCallback({
        [source]: {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      isStaffPositionSlot &&
      !isEqual(expectedCompensation.contractTypes, VACANCY_POSITION_SLOT_STAFF_CONTRACT_TYPES)
    ) {
      onChangeCallback({ [`${source}.contractTypes`]: VACANCY_POSITION_SLOT_STAFF_CONTRACT_TYPES });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaffPositionSlot, expectedCompensation.contractTypes]);

  const compensationFields = isUpdateForm ? (
    <>
      <RowGroupInputsWrapper wrapperLabel="Compensation">
        <NumberInput
          className="group-input-label"
          source={`${source}.compensation.salaryFromValue`}
          label="Min"
          value={expectedCompensation.compensation ? expectedCompensation.compensation.salaryFromValue : undefined}
          onChangeCallback={onCompensationChangeCallback}
          slotProps={{
            htmlInput: {
              min: 0,
            },
          }}
        />
        <NumberInput
          className="group-input-label"
          source={`${source}.compensation.salaryToValue`}
          label="Max"
          value={expectedCompensation.compensation ? expectedCompensation.compensation.salaryToValue : undefined}
          onChangeCallback={onCompensationChangeCallback}
          slotProps={{
            htmlInput: {
              min: 0,
            },
          }}
        />
      </RowGroupInputsWrapper>
      <Box mt={3}>
        <EnumDropdown
          source={`${source}.compensation.currency`}
          value={(() => {
            if (expectedCompensation.compensation)
              return expectedCompensation.compensation.currency === 'BYR'
                ? CURRENCY_TYPES.BYN
                : expectedCompensation.compensation.currency;

            return undefined;
          })()}
          label="Currency"
          options={COMPENSATION_CURRENCY_TYPES}
          placeholder="Select currency"
          onChangeCallback={onCompensationChangeCallback}
        />
      </Box>
    </>
  ) : null;

  return (
    <>
      <MediumFormRow sx={{ maxWidth: '50%' }}>
        <EnumDropdown
          options={LOCATION_FOR_HIRING_OPTIONS}
          source={`${source}.hiringLocation`}
          value={expectedCompensation.hiringLocation}
          label="Location for hiring"
          placeholder="Select location"
          tooltip="Office / Accommodation"
          onChangeCallback={onChangeCallback}
          isVisible={() => isHiringLocationVisible}
        />

        <EnumDropdown
          options={EMPLOYEE_CONTRACT_ASSIGMENT_TYPES}
          source={`${source}.employmentTypes`}
          value={expectedCompensation.employmentTypes}
          label="Employment type"
          multiple
          placeholder="Select employment type"
          onChangeCallback={onChangeCallback}
        />

        <EnumDropdown
          options={contractTypeOptions}
          source={`${source}.contractTypes`}
          value={expectedCompensation.contractTypes}
          label="Contract type"
          multiple
          placeholder="Select contract type"
          onChangeCallback={onChangeCallback}
          disabled={isStaffPositionSlot}
        />

        <EnumDropdown
          options={RELOCATION_OPTION}
          source={`${source}.isRelocationAvailable`}
          value={expectedCompensation.isRelocationAvailable}
          label="Relocation option"
          placeholder="Select relocation option"
          onChangeCallback={onChangeCallback}
        />

        {compensationFields}
      </MediumFormRow>
    </>
  );
};

export const WorkingConditionsInputListTemplate = (props: InputListTemplateProps) => (
  <WorkingConditionsInputs isUpdateForm={false} props={props} />
);

export const PositionSlotWorkingConditionsInputListTemplate = (props: InputListTemplateProps) => (
  <WorkingConditionsInputs isUpdateForm={false} props={props} isPositionSlotFlow />
);

export const UpdateWorkingConditionsInputListTemplate = (props: InputListTemplateProps) => (
  <WorkingConditionsInputs isUpdateForm props={props} />
);
